// src/stores/useUserGroupStore.js
import { defineStore } from "pinia";
import * as userGroupService from "@/services/userGroupService";

export const useUserGroupStore = defineStore("userGroup", {
  state: () => ({
    associations: [],
    selectedAssociation: null, // 🔥 Armazena o objeto rico para o Modal
    selectedAssociationIds: [],
    error: null,
    totalAssociationsCount: 0,
    totalAssociationsGlobalCount: 0,
    totalAssociationsPages: 1,
    currentAssociationsPage: 1,
    associationsPageSize: 10,
    associationSearchTerm: "",
  }),

  getters: {
    isAllAssociationsSelected: (state) => (total) => {
      return state.selectedAssociationIds.length === total && total > 0;
    },
  },

  actions: {
    // --- Ações de Gerenciamento de Estado da UI ---
    setSelectedAssociation(association) {
      this.selectedAssociation = association;
    },

    // 🔥 Adicionado para garantir limpeza ao fechar modais
    clearSelectedAssociation() {
      this.selectedAssociation = null;
    },

    toggleAssociationSelection(id) {
      const index = this.selectedAssociationIds.indexOf(id);
      if (index > -1) {
        this.selectedAssociationIds.splice(index, 1);
      } else {
        this.selectedAssociationIds.push(id);
      }
    },

    selectAllAssociations(ids) {
      this.selectedAssociationIds = [...ids];
    },

    clearAssociationSelection() {
      this.selectedAssociationIds = [];
    },

    // --- Ações de Interação com a API ---

    async fetchAssociations(pageNumber = 1, pageSize = 10, searchTerm = "") {
      try {
        const response = await userGroupService.fetchPaged(
          pageNumber,
          pageSize,
          searchTerm,
        );
        this.associations = response.data.data;
        this.totalAssociationsCount = response.data.totalCount;
        this.totalAssociationsGlobalCount = response.data.totalGlobalCount ?? 0;
        this.totalAssociationsPages = response.data.totalPages;
        this.currentAssociationsPage = pageNumber;
        this.associationSearchTerm = searchTerm;
        this.error = null;
      } catch (error) {
        const mensagem =
          error.response?.data?.message || "Erro ao buscar associações.";
        this.error = mensagem;
        console.error("❌ Erro ao buscar associações:", mensagem);
        this.associations = [];
        this.totalAssociationsCount = 0;
        this.totalAssociationsGlobalCount = 0;
        this.totalAssociationsPages = 1;
        throw error;
      }
    },

    async getAssociationById(id) {
      try {
        const response = await userGroupService.getById(id);
        // 🔥 CRÍTICO: Persiste o objeto detalhado (com caminhos de foto) no estado
        this.selectedAssociation = response.data;

        // Sincroniza também na lista local caso o item exista nela
        const idx = this.associations.findIndex((a) => a.userGroupId === id);
        if (idx !== -1) {
          this.associations[idx] = {
            ...this.associations[idx],
            ...response.data,
          };
        }

        return response.data;
      } catch (error) {
        const mensagem =
          error.response?.data?.message || "Erro ao obter associação.";
        this.error = mensagem;
        console.error("❌ Erro ao obter associação:", mensagem);
        throw error;
      }
    },

    async createAssociation(associationData) {
      try {
        await userGroupService.create(associationData);
        await this.fetchAssociations(
          this.currentAssociationsPage,
          this.associationsPageSize,
          this.associationSearchTerm,
        );
      } catch (error) {
        this.error =
          error.response?.data?.message || "Erro ao criar associação.";
        throw error;
      }
    },

    async deleteAssociationById(id) {
      try {
        await userGroupService.removeById(id);

        const isLastItemOnPage = this.associations.length === 1;
        const isNotFirstPage = this.currentAssociationsPage > 1;

        if (isLastItemOnPage && isNotFirstPage) {
          this.currentAssociationsPage--;
        }

        await this.fetchAssociations(
          this.currentAssociationsPage,
          this.associationsPageSize,
          this.associationSearchTerm,
        );
      } catch (error) {
        this.error =
          error.response?.data?.message || "Erro ao excluir associação.";
        throw error;
      }
    },

    // --- Ações em Lote ---

    async bulkAddByPairs(pairs) {
      try {
        await userGroupService.bulkAddByPairs(pairs);
        await this.fetchAssociations(
          this.currentAssociationsPage,
          this.associationsPageSize,
          this.associationSearchTerm,
        );
      } catch (error) {
        this.error =
          error.response?.data?.message ||
          "Erro ao adicionar associações em lote.";
        throw error;
      }
    },

    async deleteSelectedAssociations() {
      if (!this.selectedAssociationIds.length) return;
      try {
        await userGroupService.bulkRemoveByIds(this.selectedAssociationIds);

        // Ajuste de página caso deletemos todos os itens da página atual
        if (
          this.associations.length === this.selectedAssociationIds.length &&
          this.currentAssociationsPage > 1
        ) {
          this.currentAssociationsPage--;
        }

        this.clearAssociationSelection();
        await this.fetchAssociations(
          this.currentAssociationsPage,
          this.associationsPageSize,
          this.associationSearchTerm,
        );
      } catch (error) {
        this.error =
          error.response?.data?.message ||
          "Erro ao remover associações em lote.";
        throw error;
      }
    },

    async bulkAddByCartesian(data) {
      try {
        await userGroupService.bulkAddByCartesian(data);
        await this.fetchAssociations(
          this.currentAssociationsPage,
          this.associationsPageSize,
          this.associationSearchTerm,
        );
      } catch (error) {
        this.error =
          error.response?.data?.message || "Erro ao adicionar associações.";
        throw error;
      }
    },

    async bulkRemoveByCartesian(data) {
      try {
        await userGroupService.bulkRemoveByCartesian(data);
        await this.fetchAssociations(
          this.currentAssociationsPage,
          this.associationsPageSize,
          this.associationSearchTerm,
        );
      } catch (error) {
        this.error =
          error.response?.data?.message || "Erro ao remover associações.";
        throw error;
      }
    },

    // --- Ação de Busca ---
    setAssociationSearchTerm(term) {
      this.associationSearchTerm = term;
      this.currentAssociationsPage = 1;
      this.fetchAssociations(1, this.associationsPageSize, term);
    },
  },
});